import * as React from 'react';
import * as XLSX from 'xlsx';
import type { GridColDef } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import _ from 'lodash';
import {
  Box, Divider, Grid, Button, Paper, Typography, Collapse,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { mutate } from 'swr';
import globalUseStyles from '../../../globalClasses';
import ImportInput from '../../admin/stores/BulkImport/ImportInput';
import DragDrop from '../../admin/stores/BulkImport/DragDrop';

interface IBulkLocationImport {
  Id: string;
  Name: string;
  Barcode: string;
  Sequence: string;
  Station: string;
}

const exampleData : IBulkLocationImport[] = [
  {
    Id: '',
    Name: '',
    Barcode: '',
    Sequence: '',
    Station: '',
  },
];

const StoreActionMenu: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const [data, setData] = React.useState<IBulkLocationImport[]>([]);
  const globalStyles = globalUseStyles();
  const { enqueueSnackbar } = useSnackbar();

  const handleFile = (file:File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      /* Parse data */
      const ab = e?.target?.result;
      const wb = XLSX.read(ab, { type: 'array' });
      /* Get first worksheet */
      const wsName = wb.SheetNames[0];
      const ws = wb.Sheets[wsName];
      /* Convert array of arrays to json */
      const nuData = XLSX.utils.sheet_to_json<IBulkLocationImport>(ws, { defval: '' });
      // check to make sure columns from data match the example data, if not throw error and don't move to state
      if (!nuData?.[0]) {
        enqueueSnackbar('Could not parse data, please download example .csv and check your file to ensure columns match.', { persist: false, variant: 'error' });
      } else if (!_.isEqual(Object.keys(nuData[0]), Object.keys(exampleData[0]))) {
        enqueueSnackbar('There was an issue uploading your file. '
        + 'Please download example .csv and check your file to ensure columns match. '
        + `There was issues with the following columns not matching the expected order or header: ${_.difference(Object.keys(exampleData[0]), Object.keys(nuData[0])).join(', ')}`,
        { persist: false, variant: 'error' });
      // } else if (stationTypes.map((x) => label)?.filter((d) => nuData.map((x) => x.Department).findIndex((x) => x == d) > 0).length >
      // ) {
      //   console.log('broken station');
      } else {
        setData([...nuData]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleActionMenuDelete = () => {
    const dataToPost = data.map((obj) => {
      if (typeof obj.Sequence !== 'number') {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return { ...obj, Sequence: null };
      }
      return obj;
    });
    axios.post('/DestinationBin/MassUpsert', {
      BinList: dataToPost,
    })
      .then(() => {
        enqueueSnackbar('Bins upserted', { variant: 'success' });
        setData([]);
        mutate('/DestinationBin/GetDestinationBins').catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        onClose();
      })
      .catch((error: AxiosError) => {
        if (error?.message) {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        }
      });
  };

  const handleImportButton = () => {
    handleActionMenuDelete();
  };

  const exportFile = (sheetData: IBulkLocationImport[], sheetName: string) => {
    /* convert state to workbook */
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SheetJS');
    /* generate XLSX file and send to client */
    XLSX.writeFile(wb, `${sheetName}.csv`);
  };

  const testColumns: GridColDef[] = [
    {
      field: 'Id',
      headerName: 'Actions',
      flex: 0.20,
      hide: true,
    },
    { field: 'Name', headerName: 'Name', flex: 1 },
    { field: 'Barcode', headerName: 'Barcode', flex: 1 },
    { field: 'Sequence', headerName: 'Sequence', flex: 1 },
    { field: 'Station', headerName: 'Station', flex: 1 },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} />
      <Grid item xs={12}>
        <Paper elevation={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box pl={3} pt={1}>
                <Typography style={{ fontWeight: 900 }}>{`${data?.length > 0 ? 'REVIEW' : 'ADD'} DATA`}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box pl={3}>
                <Typography color="primary" variant="h6">{`Bulk Import ${data?.length > 0 ? `(${data.length})` : ''} Locations`}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box px={3}>
                <Typography>
                  {'Upload a new data set for Locations by choosing a .CSV or .XLSX file that matches the expected format.'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box pl={3}>
                <Button
                  color="primary"
                  onClick={() => exportFile(exampleData, 'exampleSheet')}
                >
                  {'Download Example CSV'}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box px={2}>
                <DragDrop handleFile={handleFile}>
                  <ImportInput handleFile={handleFile} />
                </DragDrop>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Collapse in={data.length > 0}>
                <>
                  <Divider />
                  <Box pl={3} py={2}>
                    <Typography color="primary" variant="h6">{'Locations Import Preview'}</Typography>
                  </Box>
                  <Divider />
                  <DataGrid
                    rows={data || []}
                    columns={testColumns}
                    autoHeight
                    style={{ border: 'none' }}
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    getRowId={(row) => (row.id ? (row as IBulkLocationImport).Id : (row as IBulkLocationImport).Name)}
                    className={globalStyles.transparentDataGrid}
                  />
                  <Grid item container xs={12} direction="row">
                    <Grid item>
                      <Box px={3} py={2}>
                        <Button variant="contained" color="primary" onClick={handleImportButton}>
                          {'Import'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              </Collapse>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default StoreActionMenu;
